/**
 * AI Team — 学生端课时学习页
 * 使用 MicrocoursePlayer 播放课件，支持 TTS 语音 + 逐步高亮 + 练习交互
 * 学习进度上报 → 更新 student_lessons.status
 */

import { useEffect, useState, useCallback } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { MicrocoursePlayer } from '@/components/microcourse'
import { getLessonContent, updateLessonProgress } from '@/api/aiTeam'
import { normalizeDeck } from '@ai-tutor/shared'
import type { MicrocourseProject } from '@ai-tutor/shared'
import { Button } from '@/components/ui/button'
import { ArrowLeft, Loader2, CheckCircle } from 'lucide-react'
import { ROUTES, generatePath } from '@/constants/routes'

interface LessonData {
  id: string
  courseId: string
  title: string
  objectives: string[]
  deck?: MicrocourseProject | null
  slideHtml?: string | null
  status: string
  practices?: any[]
}

export default function LessonPlayerPage() {
  const { lessonId } = useParams<{ lessonId: string }>()
  const navigate = useNavigate()

  const [lesson, setLesson] = useState<LessonData | null>(null)
  const [project, setProject] = useState<MicrocourseProject | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [completed, setCompleted] = useState(false)

  useEffect(() => {
    if (!lessonId) return
    loadLesson(lessonId)
  }, [lessonId])

  async function loadLesson(id: string) {
    setLoading(true)
    setError(null)
    try {
      const data = await getLessonContent(id)
      if (!data) {
        setError('课时不存在')
        return
      }
      setLesson(data)

      // 优先使用 deck 字段（MicrocourseProject），否则尝试转换 slideHtml
      if (data.deck) {
        setProject(normalizeDeck(data.deck, id))
      } else if (data.slideHtml) {
        // 旧格式兼容：将 slideHtml 包装为简单 MicrocourseProject
        setProject({
          id,
          title: data.title || '课时内容',
          theme: 'edu-light',
          customColors: { primary: '#2563eb', accent: '#f59e0b', bg: '#ffffff' },
          fontFamily: 'system-ui',
          transition: 'slide',
          fontScale: 1,
          chapters: [{ id: 'ch1', name: '默认章节', collapsed: false }],
          slides: [{
            id: 'slide_1',
            title: data.title || '课时内容',
            chapterId: 'ch1',
            order: 0,
            elements: [{
              id: 'el_1',
              type: 'paragraph',
              content: data.slideHtml,
            }],
            segments: [],
          }],
        })
      }

      // 标记为学习中
      if (data.status === 'not_started') {
        updateLessonProgress(id, 'in_progress').catch(() => {})
      }
      if (data.status === 'completed') {
        setCompleted(true)
      }
    } catch (err: any) {
      setError(err?.message ?? '加载失败')
    } finally {
      setLoading(false)
    }
  }

  // 学习进度上报
  const handleProgress = useCallback(
    (slideIndex: number, totalSlides: number) => {
      if (!lessonId) return
      // 当播放到最后一页时标记完成
      if (slideIndex >= totalSlides - 1) {
        updateLessonProgress(lessonId, 'completed').catch(() => {})
        setCompleted(true)
      }
    },
    [lessonId]
  )

  const handleFinish = useCallback(() => {
    if (!lessonId) return
    updateLessonProgress(lessonId, 'completed').catch(() => {})
    setCompleted(true)
  }, [lessonId])

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
      </div>
    )
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-[60vh] gap-4">
        <p className="text-red-500">{error}</p>
        <Button variant="outline" onClick={() => navigate(-1)}>
          <ArrowLeft className="w-4 h-4 mr-2" />
          返回
        </Button>
      </div>
    )
  }

  return (
    <div className="max-w-5xl mx-auto p-4 space-y-4">
      {/* Header */}
      <div className="flex items-center gap-3">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => {
            if (lesson?.courseId) {
              navigate(generatePath(ROUTES.AI_TEAM_COURSE_DETAIL, { courseId: lesson.courseId }))
            } else {
              navigate(-1)
            }
          }}
        >
          <ArrowLeft className="w-4 h-4 mr-1" />
          返回课程
        </Button>
        <h1 className="text-xl font-semibold text-slate-800 truncate">
          {lesson?.title ?? '课时学习'}
        </h1>
        {completed && (
          <span className="flex items-center gap-1 text-xs text-green-600 bg-green-50 px-2 py-1 rounded-full">
            <CheckCircle className="w-3.5 h-3.5" />
            已完成
          </span>
        )}
      </div>

      {/* Learning objectives */}
      {lesson?.objectives && lesson.objectives.length > 0 && (
        <div className="p-3 rounded-lg bg-blue-50 border border-blue-100">
          <p className="text-xs font-medium text-blue-600 mb-1">学习目标</p>
          <ul className="text-sm text-blue-700 space-y-0.5">
            {lesson.objectives.map((obj, i) => (
              <li key={i}>• {obj}</li>
            ))}
          </ul>
        </div>
      )}

      {/* Microcourse Player */}
      {project ? (
        <MicrocoursePlayer
          project={project}
          onProgressChange={handleProgress}
          onFinish={handleFinish}
          lessonId={lessonId}
          showFullscreen
          className="min-h-[500px]"
        />
      ) : (
        <div className="flex items-center justify-center h-64 text-slate-400 border rounded-xl">
          暂无课件内容
        </div>
      )}

      {/* Practice questions (if any) */}
      {lesson?.practices && lesson.practices.length > 0 && (
        <div className="p-4 rounded-xl border border-slate-200 bg-white">
          <h3 className="text-sm font-medium text-slate-700 mb-3">
            📝 课后练习 ({lesson.practices.length} 题)
          </h3>
          <div className="space-y-2">
            {lesson.practices.map((q: any, i: number) => (
              <div key={q.id ?? i} className="p-3 rounded-lg bg-slate-50 text-sm text-slate-700">
                <span className="text-xs text-slate-400 mr-2">{i + 1}.</span>
                {typeof q.content === 'string' ? q.content : JSON.stringify(q.content)}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}
